import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axiosInstance from '../axiosInstance';
import { useSelector } from 'react-redux';
import { ensureWasmReady } from '../lib/license-wasm';

const AppContext = createContext({
  gateways: [],
  savedCards: [],
  tipSettings: {
    min_amount: 1,
    max_amount: 1000,
    preset_amounts: [5, 10, 20, 50, 100],
    platform_fee: 20,
  },
  siteSettings: {
    wallet_enabled: false,
    platform_fee: 20,
  },
  walletBalance: 0,
  license: { tier: 'regular', tier_rank: 1, is_readonly: false, dev_mode: false, token: null },
  announcements: [],
  dismissAnnouncement: () => {},
  isLoading: true,
  refreshApp: () => {},
  refreshPaymentMethods: () => {},
  updateBalance: () => {},
});

export const useApp = () => useContext(AppContext);

export const AppProvider = ({ children }) => {
  const [gateways, setGateways] = useState([]);
  const [savedCards, setSavedCards] = useState([]);
  const [tipSettings, setTipSettings] = useState({
    min_amount: 1,
    max_amount: 1000,
    preset_amounts: [5, 10, 20, 50, 100],
    platform_fee: 20,
  });
  const [siteSettings, setSiteSettings] = useState({
    wallet_enabled: false,
    platform_fee: 20,
  });
  const [walletBalance, setWalletBalance] = useState(0);
  const [license, setLicense] = useState({
    tier: 'regular',
    tier_rank: 1,
    is_readonly: false,
    dev_mode: false,
    token: null,
  });
  const [announcements, setAnnouncements] = useState([]);
  const [dismissedIds, setDismissedIds] = useState(() => {
    try {
      const raw = localStorage.getItem('dismissed_announcements');
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [isLoading, setIsLoading] = useState(true);

  const token = useSelector(state => state.auth?.token);

  const fetchAppData = useCallback(async () => {
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await axiosInstance.get('/app-init');
      if (response.data.success) {
        const data = response.data.data;
        setGateways(data.gateways || []);
        setSavedCards(data.cards || []);
        setTipSettings(data.tip_settings || tipSettings);
        setSiteSettings(data.site_settings || siteSettings);
        setWalletBalance(data.wallet_balance || 0);
        if (data.license) setLicense(data.license);
      }
    } catch (error) {
      console.error('Failed to fetch app data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchAppData();
    } else {
      setIsLoading(false);
    }
  }, [token, fetchAppData]);

  // Warm the license-verifier WASM module on first mount so subsequent
  // tier checks resolve synchronously. Loading is best-effort — failures
  // fall back to AppContext.license (server-asserted state).
  useEffect(() => { ensureWasmReady(); }, []);

  const refreshPaymentMethods = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axiosInstance.get('/app-init');
      if (response.data.success) {
        const data = response.data.data;
        setSavedCards(data.cards || []);
        setGateways(data.gateways || []);
        setWalletBalance(data.wallet_balance || 0);
      }
    } catch (error) {
      console.error('Failed to refresh payment methods:', error);
    }
  }, [token]);

  const updateBalance = useCallback((delta) => {
    setWalletBalance(prev => Math.max(0, prev + delta));
  }, []);

  // Fetch active announcements for the logged-in user
  const fetchAnnouncements = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axiosInstance.get('/announcements/active');
      if (response.data.success) {
        setAnnouncements(response.data.data || []);
      }
    } catch (error) {
      // Silent — announcements are non-critical
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchAnnouncements();
    else setAnnouncements([]);
  }, [token, fetchAnnouncements]);

  const dismissAnnouncement = useCallback((id) => {
    setDismissedIds(prev => {
      const next = prev.includes(id) ? prev : [...prev, id];
      try { localStorage.setItem('dismissed_announcements', JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const visibleAnnouncements = useMemo(
    () => announcements.filter(a => !dismissedIds.includes(a.id)),
    [announcements, dismissedIds]
  );

  // Inject wallet as a virtual gateway when enabled
  const effectiveGateways = useMemo(() => {
    if (siteSettings.wallet_enabled) {
      const walletGateway = {
        name: 'wallet',
        display_name: `Wallet — $${walletBalance.toFixed(2)}`,
        supports_saved_cards: false,
        supports_one_time: true,
        is_enabled: true,
        icon: 'wallet',
        description: `Balance: $${walletBalance.toFixed(2)}`,
      };
      return [walletGateway, ...gateways];
    }
    return gateways;
  }, [gateways, siteSettings.wallet_enabled, walletBalance]);

  return (
    <AppContext.Provider
      value={{
        gateways: effectiveGateways,
        savedCards,
        tipSettings,
        siteSettings,
        walletBalance,
        license,
        announcements: visibleAnnouncements,
        dismissAnnouncement,
        isLoading,
        refreshApp: fetchAppData,
        refreshPaymentMethods,
        updateBalance,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export default AppContext;
